/**
 * Light / dark theme toggle for the sidebar footer.
 */
import * as React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Icon animation length in ms. */
const THEME_TOGGLE_ANIM_MS = 360;
/** Flip the theme this many ms before the animation ends so the icon swap feels in sync. */
const THEME_TOGGLE_ADVANCE_MS = 22;

/** Props for {@link ThemeToggle}. */
interface ThemeToggleProps {
  collapsed: boolean;
}

/** Sidebar footer control that animates the sun/moon icon then flips light/dark. */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ collapsed }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [isAnimating, setIsAnimating] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishedRef = React.useRef(true);

  /** Clear the pending flip timer on unmount so it cannot fire after teardown. */
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  /** Commit the theme change once; ignore a second call if the timer already finished. */
  const endSwitch = React.useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    toggleTheme();
    setIsAnimating(false);
  }, [toggleTheme]);

  /** Skip animation when the user prefers reduced motion; otherwise play then flip. */
  const handleClick = () => {
    if (isAnimating) return;

    const reduceMotion =
      typeof globalThis !== 'undefined' &&
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    if (reduceMotion) {
      toggleTheme();
      return;
    }

    finishedRef.current = false;
    setIsAnimating(true);
    const delayMs = Math.max(0, THEME_TOGGLE_ANIM_MS - THEME_TOGGLE_ADVANCE_MS);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      endSwitch();
    }, delayMs);
  };

  const goingToDark = !isDarkMode;
  const showMoonSpin = isAnimating && goingToDark;
  const showSunSpin = isAnimating && isDarkMode;

  const buttonContent = (
    <button
      type="button"
      onClick={handleClick}
      disabled={isAnimating}
      aria-busy={isAnimating}
      className={cn('sidebar-action-button', collapsed ? 'w-full justify-center' : 'w-full')}
      aria-label={isDarkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
    >
      {isDarkMode ? (
        <Sun
          className={cn('sidebar-action-icon', showSunSpin && 'theme-toggle-sun-click-spin')}
          size={20}
          aria-hidden
        />
      ) : (
        <Moon
          className={cn('sidebar-action-icon', showMoonSpin && 'theme-toggle-moon-click-roll')}
          size={20}
          aria-hidden
        />
      )}

      {!collapsed && (
        <span className="text-sm font-medium">{isDarkMode ? 'Mode Clair' : 'Mode Sombre'}</span>
      )}
    </button>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent side="right">
            <p>{isDarkMode ? 'Mode Clair' : 'Mode Sombre'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
};
